import React, {
  Component,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  message,
  Form,
  Input,
  Tooltip,
  Alert,
  AutoComplete,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { validPhoneNumber } from '../../global/validation';
import { triggerSmsMessage } from '../../actions/records';
import { loginAdmin } from '../../actions/userProfile';
import ModalWrapper from './ModalWrapper';
import { getRecordResponseById } from '../../actions/replies';
import {
  buildSmsTemplateOption,
  buildSmsTemplateSelectionState,
  isSmsTemplateSelectionValue,
} from '../../util/smsTemplateVariables';

const { TextArea } = Input;

class SmsMessage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      receiverType: this.props.modalType.data.receiverType,
      patientName: this.props.modalType.data.patientName,
      messageText: this.props.modalType.data.messageText,
      referringProvider: this.props.modalType.data.referringProvider,
      patientPhoneNumber: this.props.modalType.data.patientPhoneNumber,
      url: this.props.modalType.data.url,
      originalUrl: this.props.modalType.data.originalUrl,
      shortenedUrl: this.props.modalType.data.shortenedUrl,
      sendSmsFeatureEnabled: this.props.modalType.data.smsMessageFeature,
      messageTemplates: this.props.modalType.data.messageTemplates,
      resentSlug: this.props.modalType.data.resentSlug,
      smsUri: this.props.modalType.data.smsUri,
    };
  }

  send = async () => {
    const {
      hideModal, modalType: { data: { sendToken } },
    } = this.props;
    const {
      receiverType,
      patientName,
      patientPhoneNumber,
      url,
      shortenedUrl,
      resentSlug,
      smsUri,
    } = this.state;

    let { messageText } = this.state;

    // shortenedUrl is only present when resending without changing the template.
    if (url) {
      messageText = `${messageText}\n\n${url}\n\nReply STOP To Opt Out`;
    } else if (shortenedUrl) {
      messageText = `${messageText}\n\n${shortenedUrl}\n\nReply STOP To Opt Out`;
    } else {
      messageText = `${messageText}\n\nReply STOP To Opt Out`;
    }

    await this.props.triggerSmsMessage({
      sendToken,
      receiverType,
      patientName,
      messageText,
      patientPhoneNumber,
      resentSlug,
    });
    hideModal();

    // Read the send's outcome before the refetch below resets the shared records state.
    const { smsError, smsErrorMessage, optedOut } = this.props;
    await this.props.getRecordResponseById(smsUri);
    if (smsError) {
      message.error({
        content: smsErrorMessage || 'Unable to send the SMS. Please try again.',
      });
    } else if (!optedOut) {
      message.success({
        content: 'SMS Referral Message will be sent to Patient.',
      });
    } else {
      message.error({
        content: 'Number has opted out of SMS messages.',
      });
    }
  };

  handleNumberChange = (e) => {
    this.setState({ patientPhoneNumber: e.target.value });
  };

  loginAsAdmin = async () => {
    await this.props.loginAdmin();
    const { adminLoginSuccess, loginErrorMessage } = this.props;
    if (adminLoginSuccess) {
      const win = window.open(`${window.location.origin}/admin/sms_templates`, '_blank');
      win.opener = null;
    } else {
      /* global toastr */
      toastr.error(loginErrorMessage);
    }
  };

  render() {
    const { isLoading, hideModal } = this.props;
    const {
      patientPhoneNumber,
      originalUrl,
      shortenedUrl,
      sendSmsFeatureEnabled,
      messageTemplates,
      patientName,
      referringProvider,
    } = this.state;

    const { messageText } = this.state;
    const { isAdmin } = this.props;
    const smsTemplateMessage = isAdmin ? 'Click here to modify SMS template' : 'Contact your admin to edit SMS template';
    const smsTemplateOptions = messageTemplates.map((template) => buildSmsTemplateOption({ template }));

    const validatePhoneNumber = (rule, value) => {
      if (!value) {
        return Promise.reject('Please input your phone number!');
      } if (!validPhoneNumber(value)) {
        return Promise.reject('Please enter a valid phone number!');
      }
      return Promise.resolve();
    };

    const disableSubmitButton = () => {
      return (
        messageText === null
        || !sendSmsFeatureEnabled
        || patientPhoneNumber === null
        || !validPhoneNumber(patientPhoneNumber)
      );
    };

    const handleSearchTemplates = (e) => {
      // Ant Design invokes onSearch with the selected option value. Keep that
      // internal identity from replacing the rendered message set by onSelect.
      if (isSmsTemplateSelectionValue(e, smsTemplateOptions)) return;

      this.setState({ messageText: e });
    };

    const handleTemplateSelection = (selectedValue) => {
      this.setState(buildSmsTemplateSelectionState(selectedValue, messageTemplates, {
        patientName,
        referringProvider,
      }));
    };

    const customContentStyle = {
      width: '270px',
    };

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={this.send}
        actionName="Submit"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={disableSubmitButton()}
        modalTitle="Send Referral SMS"
        form="smsTemplate"
        isDraggable={true}
      >
        <Form
          layout="vertical"
          onFinish={this.send}
          id="messageText"
          fields={[
            {
              name: 'messageLink',
              value: originalUrl || shortenedUrl,
            },
          ]}
        >

          {!sendSmsFeatureEnabled && (
            <div style={{ marginBottom: '25px' }}>
              <Alert
                message="Your plan doesn't include access to send sms messages. Please upgrade your plan to make any changes."
                type="info"
                showIcon
              />
            </div>
          )}

          <div>
            <div>
              <p><i>Please confirm the information below is correct</i></p>
            </div>
            <div style={{ width: '200px', gap: '10px' }}>
              <Form.Item
                label="Patient Phone Number"
                rules={[
                  {
                    validator: validatePhoneNumber,
                  },
                ]}
                onChange={this.handleNumberChange}
                style={{ flex: 1 }}
              >
                <Input
                  placeholder="Enter phone number"
                  name="patientPhoneNumber"
                  defaultValue={patientPhoneNumber}
                  style={{ flex: 1 }}
                />
              </Form.Item>
            </div>
            <div>
              <Form.Item
                label={isAdmin
                  ? (
                    <div>
                      Message
                      <Tooltip title={smsTemplateMessage}>
                        <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} onClick={this.loginAsAdmin} />
                      </Tooltip>
                    </div>
                  ) : (
                    <div>
                      Message
                      <Tooltip title={smsTemplateMessage}>
                        <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
                      </Tooltip>
                    </div>
                  )}
              >
                <AutoComplete
                  placeholder="SMS Templates"
                  options={smsTemplateOptions}
                  onSearch={handleSearchTemplates}
                  onSelect={handleTemplateSelection}
                  filterOption={false}
                  value={this.state.messageText}
                >
                  <TextArea
                    style={{ height: 100 }}
                    value={this.state.messageText}
                    onChange={(e) => this.setState({ messageText: e.target.value })}
                  />
                </AutoComplete>
              </Form.Item>
            </div>
            <div>
              <Form.Item
                label="Message Link"
                name="messageLink"
              >
                <Input.TextArea
                  onChange={(e) => this.setState({ url: e.target.value, originalUrl: e.target.value })}
                  rows={1}
                  disabled={!!shortenedUrl} // Disable when resending without changing message template
                />
              </Form.Item>
            </div>
          </div>
        </Form>
      </ModalWrapper>
    );
  }
}

SmsMessage.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  triggerSmsMessage: PropTypes.func.isRequired,
  loginAdmin: PropTypes.func.isRequired,
  adminLoginSuccess: PropTypes.bool.isRequired,
  loginErrorMessage: PropTypes.string.isRequired,
  getRecordResponseById: PropTypes.func.isRequired,
  optedOut: PropTypes.bool,
  smsError: PropTypes.bool,
  smsErrorMessage: PropTypes.string,
};

SmsMessage.defaultProps = {
  optedOut: false,
  smsError: false,
  smsErrorMessage: null,
};

export default connect((state) => ({
  isLoading: state.records.isLoading,
  isAdmin: state.auth.isAdmin,
  loginErrorMessage: state.userProfile.loginErrorMessage,
  adminLoginSuccess: state.userProfile.loginSuccess,
  optedOut: state.records.optedOut === true,
  smsError: state.records.error === true,
  smsErrorMessage: state.records.errorMessage,
}), {
  triggerSmsMessage,
  loginAdmin,
  getRecordResponseById,
})(SmsMessage);
